/**
 * Утилиты для поиска и фильтрации данных
 */

/**
 * Нормализует строку для поиска (убирает лишние пробелы, приводит к нижнему регистру)
 */
export const normalizeSearchTerm = (term) => {
  return term?.toLowerCase().trim() || ''
}

/**
 * Проверяет, содержит ли текст поисковый запрос
 */
export const matchesSearchTerm = (text, searchTerm) => {
  if (!searchTerm) return true
  if (!text) return false
  
  const normalizedText = normalizeSearchTerm(text)
  const normalizedTerm = normalizeSearchTerm(searchTerm)
  
  return normalizedText.includes(normalizedTerm)
}

/**
 * Фильтрует отделы по поисковому запросу
 * Ищет в названии отдела и названиях/описаниях должностей
 */
export const filterDepartmentsBySearch = (departments, searchTerm) => {
  if (!searchTerm) return departments
  
  return departments.filter(department => {
    // Поиск по названию отдела
    if (matchesSearchTerm(department.name, searchTerm)) {
      return true
    }
    
    // Поиск по должностям в отделе
    if (department.positions) {
      return department.positions.some(position => 
        matchesSearchTerm(position.title, searchTerm)
      )
    }
    
    return false
  })
}

/**
 * Фильтрует должности по поисковому запросу
 */
export const filterPositionsBySearch = (positions, searchTerm) => {
  if (!searchTerm) return positions
  
  return positions.filter(position =>
    matchesSearchTerm(position.title, searchTerm)
  )
}

/**
 * Подсчитывает количество результатов поиска с фильтрацией должностей
 */
export const getFilteredSearchResultsCount = (departments, searchTerm) => {
  if (!searchTerm) {
    // При пустом поиске возвращаем общее количество
    const totalPositions = departments.reduce((acc, dept) => {
      return acc + (dept.positions ? dept.positions.length : 0)
    }, 0)
    return { departments: departments.length, positions: totalPositions }
  }
  
  const filteredDepartments = filterDepartmentsWithFilteredPositions(departments, searchTerm)
  const totalPositions = filteredDepartments.reduce((acc, dept) => {
    return acc + dept.positions.length
  }, 0)
  
  return {
    departments: filteredDepartments.length,
    positions: totalPositions
  }
}

/**
 * Подсчитывает количество результатов поиска в отделах (старая версия)
 */
export const getSearchResultsCount = (departments, searchTerm) => {
  if (!searchTerm) {
    // При пустом поиске возвращаем общее количество
    const totalPositions = departments.reduce((acc, dept) => {
      return acc + (dept.positions ? dept.positions.length : 0)
    }, 0)
    return { departments: departments.length, positions: totalPositions }
  }
  
  const filteredDepartments = filterDepartmentsBySearch(departments, searchTerm)
  const totalPositions = filteredDepartments.reduce((acc, dept) => {
    if (dept.positions) {
      const filteredPositions = filterPositionsBySearch(dept.positions, searchTerm)
      return acc + filteredPositions.length
    }
    return acc
  }, 0)
  
  return {
    departments: filteredDepartments.length,
    positions: totalPositions
  }
}

/**
 * Фильтрует отделы с фильтрацией должностей внутри каждого отдела
 * Ищет только в должностях (названиях и описаниях)
 * Возвращает отделы только с релевантными должностями
 */
export const filterDepartmentsWithFilteredPositions = (departments, searchTerm) => {
  if (!searchTerm) return departments
  
  if (!Array.isArray(departments)) return []
  
  return departments
    .map(department => {
      // Проверяем что отдел существует
      if (!department) return null
      
      // Фильтруем должности в отделе
             const filteredPositions = Array.isArray(department.positions)
        ? department.positions.filter(position =>
            position && matchesSearchTerm(position.title, searchTerm)
          )
        : []
      
      // Возвращаем отдел с отфильтрованными должностями
      return {
        ...department,
        positions: filteredPositions
      }
    })
    .filter(department => department && department.positions && department.positions.length > 0) // Убираем отделы без найденных должностей
}

/**
 * Проверяет, есть ли совпадения в должности по поисковому запросу
 * Возвращает объект с информацией о том, что именно совпало
 */
export const getPositionSearchMatch = (position, searchTerm) => {
  if (!searchTerm) return { hasMatch: false }
  
  const titleMatch = matchesSearchTerm(position.title, searchTerm)
  
  return {
    hasMatch: titleMatch,
    titleMatch,
    descriptionMatch: false
  }
} 

/**
 * Фильтрует отделы по поисковому запросу с учетом отдельного массива должностей
 * Ищет в названии отдела и названиях должностей во всех отделах
 */
export const filterDepartmentsBySearchWithPositions = (departments, positions, searchTerm) => {
  if (!searchTerm) return departments
  
  return departments.filter(department => {
    // Поиск по названию отдела
    if (matchesSearchTerm(department.name, searchTerm)) {
      return true
    }
    
    // Поиск по должностям в данном отделе (только по названию)
    const departmentPositions = positions.filter(pos => pos.department_id === department.id)
    return departmentPositions.some(position => 
      matchesSearchTerm(position.title, searchTerm)
    )
  })
}

/**
 * Подсчитывает количество результатов поиска с отдельным массивом должностей
 */
export const getSearchResultsCountWithPositions = (departments, positions, searchTerm) => {
  if (!searchTerm) {
    return { 
      departments: departments.length, 
      positions: positions.length 
    }
  }
  
  const filteredDepartments = filterDepartmentsBySearchWithPositions(departments, positions, searchTerm)
  
  // Подсчитываем найденные должности во всех отделах (только по названию)
  let foundPositions = 0
  for (const department of filteredDepartments) {
    const departmentPositions = positions.filter(pos => pos.department_id === department.id)
    foundPositions += departmentPositions.filter(position => 
      matchesSearchTerm(position.title, searchTerm)
    ).length
  }
  
  return {
    departments: filteredDepartments.length,
    positions: foundPositions
  }
} 